/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  KnownMembership,
  Method,
  type MatrixClient,
  type Room,
  RoomEvent,
} from "matrix-js-sdk";

export type InviteTokenVisibility = "public" | "private";

export interface PublicInviteTokenResponse {
  room_id: string;
  room_name: string | null;
  room_avatar_url: string | null;
  visibility: InviteTokenVisibility;
  created_by: string;
  created_by_display_name: string | null;
  created_by_avatar_url: string | null;
  is_active: boolean;
}

export interface ApplyInviteTokenResponse {
  room_id: string;
  invited_user_id: string;
  invited_by: string;
  via_token: boolean;
  membership: "invite" | "join";
}

const INVITE_TOKEN_PREFIX =
  "/_synapse/client/ru.avagroup.klava.room_lifecycle/token";
const INVITE_MEMBERSHIP_TIMEOUT_MS = 10_000;

const pendingPublicRequests = new Map<
  string,
  Promise<PublicInviteTokenResponse>
>();
const pendingApplyRequests = new WeakMap<
  MatrixClient,
  Map<string, Promise<ApplyInviteTokenResponse>>
>();

export type InviteTokenErrorKind =
  | "invalid"
  | "service_unavailable"
  | "validation_failed"
  | "apply_failed"
  | "missing_homeserver"
  | "missing_token";

/** Ошибка запроса invite gateway с типизированной причиной. */
export class InviteTokenRequestError extends Error {
  public constructor(
    public readonly kind: InviteTokenErrorKind,
    public readonly status?: number,
  ) {
    super(kind);
    this.name = "InviteTokenRequestError";
  }

  public get retryable(): boolean {
    return this.status === undefined || this.status >= 500;
  }

  public get invalid(): boolean {
    return this.kind === "invalid";
  }
}

function getTokenPath(token: string): string {
  return `${INVITE_TOKEN_PREFIX}/${encodeURIComponent(token)}`;
}

/**
 * Публично проверяет токен приглашения на указанном homeserver.
 */
export async function getPublicInviteToken(
  homeserverUrl: string,
  token: string,
): Promise<PublicInviteTokenResponse> {
  const url = new URL(getTokenPath(token), homeserverUrl).toString();
  const existing = pendingPublicRequests.get(url);
  if (existing) return existing;

  const request = fetch(url, { headers: { Accept: "application/json" } })
    .then(async (response) => {
      if (!response.ok) {
        throw new InviteTokenRequestError(
          response.status >= 500 ? "service_unavailable" : "invalid",
          response.status,
        );
      }
      return (await response.json()) as PublicInviteTokenResponse;
    })
    .catch((error: unknown) => {
      if (error instanceof InviteTokenRequestError) throw error;
      throw new InviteTokenRequestError("validation_failed");
    })
    .finally(() => pendingPublicRequests.delete(url));

  pendingPublicRequests.set(url, request);
  return request;
}

/**
 * Применяет токен к текущей сессии. Параллельные вызовы дедуплицируются,
 * чтобы React StrictMode не отправлял приглашение повторно.
 */
export async function applyInviteToken(
  client: MatrixClient,
  token: string,
  roomId: string,
): Promise<ApplyInviteTokenResponse> {
  let clientRequests = pendingApplyRequests.get(client);
  if (!clientRequests) {
    clientRequests = new Map();
    pendingApplyRequests.set(client, clientRequests);
  }

  const existing = clientRequests.get(token);
  if (existing) return existing;

  const request = applyInviteTokenAndWaitForMembership(client, token, roomId)
    .catch((error: unknown) => {
      if (error instanceof InviteTokenRequestError) throw error;

      const status = getHttpStatus(error);
      throw new InviteTokenRequestError(
        status !== undefined && status >= 400 && status < 500
          ? "invalid"
          : "apply_failed",
        status,
      );
    })
    .finally(() => clientRequests.delete(token));

  clientRequests.set(token, request);
  return request;
}

async function applyInviteTokenAndWaitForMembership(
  client: MatrixClient,
  token: string,
  roomId: string,
): Promise<ApplyInviteTokenResponse> {
  const membershipWait = beginMembershipWait(client, roomId);

  try {
    const response = await client.http.authedRequest<ApplyInviteTokenResponse>(
      Method.Post,
      `${getTokenPath(token)}/apply`,
      {},
      undefined,
      { prefix: "" },
    );
    membershipWait.armTimeout();
    await membershipWait.promise;
    return response;
  } catch (error) {
    membershipWait.cancel();
    throw error;
  }
}

interface MembershipWaitHandle {
  promise: Promise<void>;
  armTimeout: () => void;
  cancel: () => void;
}

/**
 * Начинает ожидание синхронизации invite/join membership до отправки POST.
 */
function beginMembershipWait(
  client: MatrixClient,
  roomId: string,
): MembershipWaitHandle {
  let timeoutId: number | undefined;
  let finished = false;
  let resolveWait!: () => void;

  const finish = (): void => {
    if (finished) return;
    finished = true;
    client.removeListener(RoomEvent.MyMembership, onMembership);
    window.clearTimeout(timeoutId);
    resolveWait();
  };

  const hasUsableMembership = (membership: string | undefined): boolean =>
    membership === KnownMembership.Invite ||
    membership === KnownMembership.Join;

  const onMembership = (room: Room, membership: string): void => {
    if (room.roomId === roomId && hasUsableMembership(membership)) finish();
  };

  const promise = new Promise<void>((resolve) => {
    resolveWait = resolve;
    client.on(RoomEvent.MyMembership, onMembership);

    if (hasUsableMembership(client.getRoom(roomId)?.getMyMembership())) {
      finish();
    }
  });

  return {
    promise,
    armTimeout: (): void => {
      if (finished || timeoutId !== undefined) return;
      timeoutId = window.setTimeout(finish, INVITE_MEMBERSHIP_TIMEOUT_MS);
    },
    cancel: finish,
  };
}

function getHttpStatus(error: unknown): number | undefined {
  if (
    typeof error === "object" &&
    error !== null &&
    "httpStatus" in error &&
    typeof error.httpStatus === "number"
  ) {
    return error.httpStatus;
  }
  return undefined;
}
